import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile, hScroll } from '../utils/useMobile';
import { T } from '../v2/theme';

// 대표 링크 가입 업체 — '연결 확인 대기' 패널 + 연결 확인 시트 (YK 대표 링크 2026-11-03, 시안 YkShareWeb · YkShareConfirm).
//
// 대표 링크로 가입해 오토덱스 승인을 마친 업체를 거래처에 연결한다(자동 연결 없음 — 최종 대상은 대행사가 고른다).
//   GET  /agency-join-requests?status=waiting|declined → {items:[…candidates], counts:{waiting, declined}}
//   POST /agency-join-requests/:id/link    {unitId} | {accountId}(대행사 → 본인 몫) | {newClient:true}
//   POST /agency-join-requests/:id/decline — '연결 안 함' 은 계정을 지우지 않는다(나중에 다시 [연결]).
// 대기 0 · 연결 안 함 0 이면 패널을 그리지 않는다. 409(다른 곳에서 먼저 처리) = 시트를 닫고 목록을 다시 읽는다.
// 운영자 전용(직원 403) — 노출 판정은 호출부(CancelAgencyClients).
// props: { scope ('' | admin 이 고른 agencyId), agencyName, pickUnits [{id, name, bizNo, sub}] 미연결·활성 단위, showToast, onLinked }

var REASON = { bizNo: '사업자번호 같음', name: '이름 같음' };
var REASON_LONG = { bizNo: '사업자번호가 같은 거래처', name: '이름이 같은 거래처' };
var OTHER_MAX = 8;

// KST 'YYYY-MM-DD HH:MM:SS' → 'MM/DD HH:MM'
function shortStamp(s) { var v = String(s || ''); return v.length >= 16 ? v.slice(5, 16).replace('-', '/') : (v || '—'); }
// 가입 칸 = 오토덱스 승인 시각. 거절 후 복구된 회사는 approvedAt 이 null 일 수 있다 → 가입 요청 시각(createdAt)으로.
function joinedAt(it) { return shortStamp(it.approvedAt || it.createdAt); }

function CancelAgencyJoinRequests(props) {
  var isMobile = useIsMobile();
  var showToast = props.showToast || function () {};
  var scopeQs = props.scope ? '&agencyId=' + encodeURIComponent(props.scope) : '';

  var [data, setData] = useState(null);           // {items, counts} — 대기 목록
  var [declOpen, setDeclOpen] = useState(false);
  var [declined, setDeclined] = useState(null);   // 연결 안 함 목록 (펼칠 때 읽음)
  var [target, setTarget] = useState(null);       // 시트에 연 가입 요청
  var [choice, setChoice] = useState(null);       // 'c0'… 후보 · 'other' · 'new'
  var [otherQ, setOtherQ] = useState('');
  var [otherId, setOtherId] = useState(0);
  var [busy, setBusy] = useState(false);

  var fetchList = function (status) {
    return authFetch(API + '/agency-join-requests?status=' + status + scopeQs)
      .then(function (r) { return r.json(); })
      .then(function (d) { return d && d.success ? d : null; });
  };
  var load = function () {
    fetchList('waiting').then(function (d) { if (d) setData({ items: d.items || [], counts: d.counts || {} }); }).catch(function () {});
    if (declOpen) fetchList('declined').then(function (d) { if (d) setDeclined(d.items || []); }).catch(function () {});
  };
  useEffect(function () { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var toggleDeclined = function () {
    var next = !declOpen;
    setDeclOpen(next);
    if (next) fetchList('declined').then(function (d) { setDeclined(d ? (d.items || []) : []); }).catch(function () { setDeclined([]); });
  };

  var open = function (it) {
    var cands = it.candidates || [];
    var pre = -1;
    cands.forEach(function (c, i) { if (c.preselect && pre < 0) pre = i; });
    setTarget(it);
    setChoice(pre >= 0 ? 'c' + pre : null); // 미리 고를 후보가 없으면 직접 고르게 (앱과 같음 — 새 거래처 잘못 만들기 방지)
    setOtherQ(''); setOtherId(0);
  };
  var close = function () { if (!busy) setTarget(null); };

  var post = function (path, body, okMsg, linked) {
    if (busy) return;
    setBusy(true);
    if (props.scope) body.agencyId = parseInt(props.scope, 10);
    authFetch(API + '/agency-join-requests/' + target.id + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { status: r.status, d: d || {} }; }); })
      .then(function (x) {
        setBusy(false);
        if (x.d.success) {
          showToast(okMsg); setTarget(null); load();
          if (linked && props.onLinked) props.onLinked();
          return;
        }
        showToast(x.d.error || '처리 실패', 'err');
        // 409 = 다른 곳에서 먼저 처리(REQUEST_CHANGED) · 그 거래처가 먼저 연결됨(UNIT_UNAVAILABLE) — 대기 목록과 거래처 목록('다른 거래처 고르기') 새로 읽기
        if (x.status === 409) { setTarget(null); load(); if (props.onLinked) props.onLinked(); }
      })
      .catch(function () { setBusy(false); showToast('처리 실패', 'err'); });
  };
  var cands = target ? (target.candidates || []) : [];
  var linkBody = function () {
    if (choice === 'new') return { newClient: true };
    if (choice === 'other') return !otherId ? null : otherId < 0 ? { accountId: -otherId } : { unitId: otherId }; // 음수 = 본인 몫 없는 대행사 계정
    var c = choice ? cands[parseInt(choice.slice(1), 10)] : null;
    if (!c) return null;
    return c.unitId ? { unitId: c.unitId } : { accountId: c.accountId };
  };
  var doLink = function () { var b = linkBody(); if (b) post('/link', b, target.companyName + ' 연결했습니다', true); };
  var doDecline = function () { post('/decline', {}, '연결 안 함으로 옮겼습니다', false); };

  // ── 표 ──
  var TH = { textAlign: 'left', color: T.muted, fontWeight: 600, fontSize: 12, padding: '9px 14px', borderBottom: '1px solid ' + T.bdSoft, background: T.head, whiteSpace: 'nowrap' };
  var TD = { padding: '10px 14px', borderBottom: '1px solid ' + T.bdSoft, verticalAlign: 'middle', fontSize: 13 };
  var mute = { color: T.faint };
  var found = function (it) {
    var c = (it.candidates || []).filter(function (x) { return x.preselect; })[0];
    if (c) return <span style={{ color: T.ok, fontWeight: 600 }}>{c.name + ' (' + (REASON[c.reason] || '') + ')'}</span>;
    if ((it.candidates || []).length) return <span style={mute}>{'후보 ' + it.candidates.length + '곳'}</span>;
    return <span style={mute}>찾지 못함 · 새 거래처</span>;
  };
  var btn = { border: 0, borderRadius: 8, background: T.navy, color: T.card, fontSize: 12.5, fontWeight: 700, padding: '0 12px', height: isMobile ? 40 : 32, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  var table = function (items, label) {
    return (<div style={isMobile ? hScroll : undefined}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 640 : undefined }}>
        <thead><tr><th style={TH}>업체명</th><th style={TH}>사업자번호</th><th style={TH}>대표 · 연락처</th><th style={TH}>찾은 거래처</th><th style={TH}>가입</th><th style={TH} /></tr></thead>
        <tbody>{items.map(function (it) {
          return (<tr key={it.id} data-join-id={it.id}>
            <td style={TD}><b>{it.companyName}</b></td>
            <td style={TD}>{it.bizNo || <span style={mute}>—</span>}</td>
            <td style={TD}>{[it.ceoName, it.ceoPhone].filter(Boolean).join(' · ')}</td>
            <td style={TD}>{found(it)}</td>
            <td style={Object.assign({}, TD, mute)}>{joinedAt(it)}</td>
            <td style={Object.assign({}, TD, { textAlign: 'right' })}><button type="button" style={btn} onClick={function () { open(it); }}>{label}</button></td>
          </tr>);
        })}</tbody>
      </table>
    </div>);
  };

  var counts = (data && data.counts) || {};
  var waiting = (data && data.items) || [];
  var nDecl = parseInt(counts.declined, 10) || 0;
  var showPanel = !!data && (waiting.length > 0 || nDecl > 0);

  // ── 연결 확인 시트 ──
  var optSt = function (on) {
    return { display: 'flex', alignItems: 'center', gap: 12, border: '1.5px solid ' + (on ? T.accent : T.bd), background: on ? T.accentTint : T.card,
      borderRadius: 12, padding: '11px 14px', cursor: 'pointer' };
  };
  var opt = function (key, title, sub) {
    var on = choice === key;
    return (<label key={key} style={optSt(on)}>
      <input type="radio" name="join-target" checked={on} onChange={function () { setChoice(key); }} style={{ margin: 0, accentColor: T.accent }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12, color: T.muted, marginTop: 2 }}>{sub}</span>
      </span>
    </label>);
  };
  var oq = otherQ.trim().toLowerCase();
  var otherHits = (props.pickUnits || []).filter(function (u) {
    return !oq || String(u.name || '').toLowerCase().indexOf(oq) >= 0 || String(u.bizNo || '').toLowerCase().indexOf(oq) >= 0;
  }).slice(0, OTHER_MAX);
  var kv = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', background: T.head, borderRadius: 12, padding: '12px 14px', fontSize: 13.5 };
  var k = { color: T.muted };
  var v = { fontWeight: 600, color: T.ink, minWidth: 0, wordBreak: 'break-all' };
  var sheet = target ? (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 9999, padding: isMobile ? 0 : 16 }} onClick={close}>
      <div role="dialog" aria-label="거래처 연결 확인" onClick={function (e) { e.stopPropagation(); }}
        style={{ background: T.card, borderRadius: isMobile ? '16px 16px 0 0' : 14, padding: isMobile ? '16px 14px 24px' : 20, width: 520, maxWidth: isMobile ? '100%' : '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 10, boxSizing: 'border-box' }}>
        <h3 style={{ margin: 0, fontSize: 16, color: T.navy }}>거래처 연결 확인</h3>
        <div style={kv}>
          <span style={k}>업체명</span><span style={v}>{target.companyName}</span>
          <span style={k}>사업자번호</span><span style={v}>{target.bizNo || '—'}</span>
          <span style={k}>대표</span><span style={v}>{[target.ceoName, target.ceoPhone].filter(Boolean).join(' · ')}</span>
          <span style={k}>가입</span><span style={v}>{joinedAt(target) + ' · 대표 링크 · 오토덱스 승인됨'}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.dim, marginTop: 2 }}>연결할 거래처</div>
        {cands.map(function (c, i) { return opt('c' + i, c.name, (REASON_LONG[c.reason] || '') + (c.label ? ' · ' + c.label : '')); })}
        {opt('other', '다른 거래처 고르기', '이름이 달라 못 찾은 경우')}
        {choice === 'other' && (<div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 4 }}>
          <input value={otherQ} onChange={function (e) { setOtherQ(e.target.value); }} placeholder="거래처명·사업자번호 검색"
            style={{ border: '1px solid ' + T.line, borderRadius: 8, padding: '9px 11px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' }} />
          {otherHits.length === 0 ? <div style={{ fontSize: 12.5, color: T.faint, padding: '4px 2px' }}>연결할 수 있는 거래처가 없습니다</div>
            : otherHits.map(function (u) {
              var on = otherId === u.id;
              return (<button key={u.id} type="button" data-pick-unit={u.id} onClick={function () { setOtherId(u.id); }}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 8, textAlign: 'left', border: '1px solid ' + (on ? T.accent : T.bd), background: on ? T.accentTint : T.card,
                  borderRadius: 8, padding: '8px 11px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: on ? 700 : 500, color: T.ink }}>
                <span>{u.name}</span><span style={{ color: T.faint, fontWeight: 400 }}>{u.sub}</span>
              </button>);
            })}
        </div>)}
        {opt('new', '새 거래처로 추가', (props.agencyName ? props.agencyName + ' ' : '') + '기본 단가를 따릅니다')}
        {choice === 'new' && cands.length > 0 && (<div style={{ fontSize: 12.5, color: T.warn, background: T.warnTint, borderRadius: 8, padding: '8px 11px' }}>
          위에 같은 업체로 보이는 거래처가 있습니다. 같은 업체라면 그 거래처로 연결해 주세요.
        </div>)}
        <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.55 }}>
          연결하면 그 거래처의 지난 신청과 정산도 이 업체에 보이고, 이때부터 말소를 신청할 수 있습니다. [연결 안 함]을 눌러도 업체 계정은 오토덱스 고객으로 남고, 나중에 다시 연결할 수 있습니다.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          <button className="btn btn-outline btn-sm" style={isMobile ? { minHeight: 40 } : undefined} disabled={busy} onClick={close}>닫기</button>
          <div style={{ flex: 1 }} />
          {target.status === 'waiting' && <button className="btn btn-outline btn-sm" style={isMobile ? { minHeight: 40 } : undefined} disabled={busy} onClick={doDecline}>연결 안 함</button>}
          <button className="btn btn-blue btn-sm" style={isMobile ? { minHeight: 40 } : undefined} disabled={busy || !linkBody()} onClick={doLink}>연결</button>
        </div>
      </div>
    </div>
  ) : null;

  return (<React.Fragment>
    {showPanel && (<div data-part="join-panel" style={{ background: T.card, border: '1px solid ' + T.warnBd, borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
      {waiting.length > 0 && (<React.Fragment>
        <div style={{ background: T.warnTint, padding: '10px 16px', fontSize: 13.5, fontWeight: 700, color: T.warn }}>
          연결 확인 대기 {waiting.length} · 대표 링크로 가입해 오토덱스 승인을 마친 업체
        </div>
        {table(waiting, '연결 확인')}
      </React.Fragment>)}
      {nDecl > 0 && (<button type="button" aria-expanded={declOpen ? 'true' : 'false'} onClick={toggleDeclined}
        style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderTop: waiting.length ? '1px solid ' + T.bdSoft : 0, background: T.head,
          padding: '10px 16px', fontSize: 13, color: T.dim, cursor: 'pointer', fontFamily: 'inherit' }}>
        연결 안 함 {nDecl} · 나중에 다시 연결할 수 있습니다 {declOpen ? '▴' : '▾'}
      </button>)}
      {nDecl > 0 && declOpen && (declined ? table(declined, '연결') : <div style={{ padding: '10px 16px', fontSize: 12.5, color: T.faint }}>불러오는 중…</div>)}
    </div>)}
    {sheet}
  </React.Fragment>);
}

export default CancelAgencyJoinRequests;
